import { useDispatch } from "react-redux";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import { CellContext, HeaderContext } from "@tanstack/react-table";

import { GroupOverviewData } from "../../GroupPage.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    box: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        cursor: "pointer",
        "& i": {
            padding: theme.spacing(2),
            width: theme.spacing(4),
            height: theme.spacing(4),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
    },
}));

type ExpanderButtonProps = {
    isExpanded: boolean;
    onClick: React.MouseEventHandler<HTMLDivElement>;
    depth?: number;
};
const ExpanderButton = ({ depth = 0, ...props }: React.PropsWithChildren<ExpanderButtonProps>) => {
    const { classes } = useStyles();
    const theme = useTheme();

    return (
        <div onClick={props.onClick} className={classes.box} style={{ paddingLeft: `${depth * 2}rem` }}>
            <div style={{ padding: theme.spacing(2) }}>
                {props.isExpanded ? <i className="fa-solid fa-angle-down" /> : <i className="fa-sm fa-solid fa-chevron-right" />}
            </div>
            {props.children}
        </div>
    );
};
export const ExpandedCell = (
    props: React.PropsWithChildren<CellContext<GroupOverviewData, unknown> & { shouldOpenSidePanel?: boolean }>,
) => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const onClick = () => {
        if (props.shouldOpenSidePanel) {
            dispatch(
                showDetails({
                    type: "group",
                    selectedId: props.row.original.id,
                }),
            );
        }
    };

    if (!props.row.getCanExpand()) {
        return (
            <Box onClick={onClick} style={{ marginLeft: theme.spacing(10), paddingLeft: `${props.row.depth * 2}rem` }}>
                {props.children}
            </Box>
        );
    }

    return (
        <ExpanderButton
            isExpanded={props.row.getIsExpanded()}
            onClick={props.row.getToggleExpandedHandler()}
            depth={props.row.depth}
        >
            {props.children}
        </ExpanderButton>
    );
};

type ExpandedHeaderProps = HeaderContext<GroupOverviewData, unknown> & { title: string };
export const ExpandedHeader = (props: ExpandedHeaderProps) => {
    const { classes } = useStyles();

    const onClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        e.stopPropagation();
        props.table.toggleAllRowsExpanded();
    };

    return (
        <div className={classes.box}>
            <ExpanderButton onClick={onClick} isExpanded={props.table.getIsAllRowsExpanded()} />
            <Typography
                component="div"
                variant="body1"
                style={{ fontWeight: 600 }}
                onClick={props.column.getToggleSortingHandler()}
            >
                {props.title}
            </Typography>
        </div>
    );
};
